import * as React from 'react';
import { Tooltip } from 'react-tooltip';
import Modal from './modal';
import Alert, { AlertCategoryEnum } from './Alert';
import { SelectableFixedComment } from '../../PulmonaryFunctionTests/Shared/UseFixedComments';
import { SetStateAction } from 'react';

export interface CommentsDialogProps {
    header: string;
    show: boolean;
    editable?: boolean;
    errorMessage?: string;
    items: Array<SelectableFixedComment>;
    showFilter?: boolean;
    onChange: (id: string, checked: boolean) => void;
    onClose: () => void;
}

export interface CommentsSelectorProps {
    onOk: () => void;
    editable?: boolean;
    errorMessage?: string;
    showFilter?: boolean;
    items: Array<SelectableFixedComment>;
    onChange: (id: string, checked: boolean) => void;
    selectedComments: Array<SelectableFixedComment>;
    setSelectedComments: React.Dispatch<SetStateAction<SelectableFixedComment[]>>;
}

export const CommentsDialog = (props: CommentsDialogProps) => {
    const { header, show, onClose } = props;
    const [selectedComments, setSelectedComments] = React.useState<SelectableFixedComment[]>([]);

    React.useEffect(() => {
        setSelectedComments(props.items.map(item => ({ ...item })));
    }, [props.items]);

    const handleCloseWithoutSave = () => {
        setSelectedComments(props.items.map(item => ({ ...item })));
        props.onClose();
    };

    const handleOkClick = () => {
        selectedComments.forEach(row => {
            props.onChange(row.id, row.selected);
        });
        props.onClose();
    };

    return (
        <Modal show={show} header={header} closeModalHandler={handleCloseWithoutSave}>
            <CommentsSelector {...props} onOk={handleOkClick} selectedComments={selectedComments} setSelectedComments={setSelectedComments} />
        </Modal>
    );
}

export const CommentsSelector = ({ onOk, items, onChange, errorMessage = null, editable = true, showFilter = false, setSelectedComments, selectedComments }: CommentsSelectorProps) => {
    const [filterText, setFilterText] = React.useState();

    React.useEffect(() => {
        setSelectedComments(items.map(item => ({ ...item })));
    }, [items]);

    const onTextFilter = (event) => {
        setFilterText(event.target.value.toLowerCase());
    }

    const handleCheckboxChange = (commentId: string) => {
        setSelectedComments(prev =>
            prev.map(row => {
                if (row.id === commentId) {
                    return {
                        ...row,
                        selected: !row.selected
                    };
                }
                return row;
            })
        );
    };

    const filteredItems = items.filter(i => filterText === undefined || i.comment.toLowerCase().includes(filterText));

    const hideFixedComment = (fixedComment: SelectableFixedComment): boolean => {
        return fixedComment.disabled || !fixedComment.selected || !fixedComment.followOnCommentAvailable;
    };

    const renderItems = () => {
        if (filteredItems.length > 0) {
            return filteredItems.sort((a, b) => a.displayOrder - b.displayOrder).map(i => {
                return (
                    <React.Fragment key={i.id}>
                        {i.description ? <Tooltip id={`${i.id}descriptionTooltip`} style={{ zIndex: 2500 }}>
                            {i.description}
                        </Tooltip> : null}
                        <div className="checkbox" data-tooltip-id={`${i.id}descriptionTooltip`}>
                            <label htmlFor={i.id}>
                        <input id={i.id} type="checkbox" disabled={!editable} checked={selectedComments.find(c => c.id === i.id)?.selected ?? false} onChange={() => handleCheckboxChange(i.id)} />
                                {i.comment}
                            </label>
                        </div>
                        <div hidden={hideFixedComment(i)}>
                            <label htmlFor={`comment-annotation-${i.id}`}>{i.followOnCommentAnnotation}</label>
                            <input className="form-control" id={`comment-annotation-${i.id}`} value={i.followOnComment} disabled={!editable} checked={i.selected} onChange={(e) => onChange(e.target.id, e.target.checked)} />
                        </div>
                    </React.Fragment>
                );
            })
        } else {
            return (
                <Alert show={true} category={AlertCategoryEnum.Danger}>
                    There are no fixed comments available
                </Alert>
            );
        }
    }

    return (
        <>
            <div className="modal-body">
                <Alert show={errorMessage !== null && selectedComments.filter(s => s.selected).length === 0} category={AlertCategoryEnum.Danger}>
                    {errorMessage}
                </Alert>
                {showFilter ? <div className="form-group">
                    <input type="text" className="form-control" placeholder="Filter..." onChange={onTextFilter} />
                </div> : null}
                {renderItems()}
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" data-automationid="submit-modal" disabled={errorMessage !== null && selectedComments.filter(s => s.selected).length === 0} onClick={() => onOk()}>OK</button>
            </div>
        </>
    );
}
